import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { getCurrentUser } from "../../utils/auth";

/**
 * Loads the Action Center and owns its optimistic dismissals.
 *
 * ── WHY IT LIVES IN THE SHELL AND NOT ON THE DASHBOARD ──────────────────────
 * The first cut of this fetched from a panel mounted on `/dashboard`, which
 * quietly meant the guidance only reached people who open the dashboard. An
 * admin who spends their whole day in `/enquiries` — which is most
 * counsellors — never saw a single prompt. Mounted in `TopNav` it follows them
 * across every page, and it costs the dashboard nothing.
 *
 * ── WHY THE DISMISS IS OPTIMISTIC ───────────────────────────────────────────
 * Clicking ✕ on a row is the one interaction here that must feel instant: the
 * user has decided the row is noise, and leaving it on screen for a round trip
 * reads as "the button did not work" — which is how people learn to stop
 * pressing it. A failed request restores the row rather than swallowing the
 * error, since a dismissal that silently did not persist would come back on the
 * next load anyway and look like a bug.
 */
export default function useActionCenter() {
  const { selectedBranchId } = useBranch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Rows removed locally and not yet confirmed by the server. Kept out of
  // `data` so a refetch cannot resurrect one mid-flight.
  const [pending, setPending] = useState(() => new Set());

  // Bumped by `reload()`. The fetch keys off it rather than living in a
  // callback, so there is exactly one place the request is made — see the
  // StrictMode note below for why that matters.
  const [nonce, setNonce] = useState(0);

  // The endpoint is `roleCheck('superadmin', 'admin')`, so asking as anyone
  // else is a guaranteed 403. Checked here rather than at the call site so a
  // second mount cannot forget.
  const usertype = getCurrentUser()?.usertype;
  const enabled = usertype === "superadmin" || usertype === "admin";

  /**
   * ⚠️ **The cancellation flag is scoped to ONE effect run, and it has to be.**
   *
   * This was first written as a `mounted` ref cleared in an effect cleanup, and
   * it shipped a permanent loading state. `src/index.js` wraps the app in
   * `React.StrictMode`, so React 18 mounts → cleans up → mounts again; that ref
   * went `false` on the simulated unmount and was never restored, so every
   * later `setLoading(false)` was skipped. It would have behaved correctly in a
   * production build, which is the worst possible way for it to be wrong.
   *
   * A `let cancelled` inside the effect cannot fail that way: the second run
   * closes over a fresh one. Nothing outside an effect run needs to know
   * whether the component is mounted — a `setState` after unmount is a no-op in
   * React 18, not a leak.
   */
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);

    apiClient
      .get("/dashboard/action-center")
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        // Additive surface. If it cannot load, nothing about the app changes —
        // show no pill rather than an error nobody can act on.
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, selectedBranchId, nonce]);

  /**
   * Re-read the counts.
   *
   * Called when the drawer opens, which is the one moment the numbers are about
   * to be read closely. Without it an admin who assigns twelve leads and comes
   * back is told they still have twelve — and a to-do list that does not notice
   * work being done is one people stop believing.
   */
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const dismiss = useCallback(async (id, days) => {
    setPending((prev) => new Set(prev).add(id));
    try {
      await apiClient.post("/dashboard/action-center/dismiss", { id, days });
    } catch {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  return useMemo(() => {
    const signals = (data?.signals || []).filter((s) => !pending.has(s.id));
    const setup =
      data?.setup && !data.setup.dismissed && !pending.has("setup.guide")
        ? data.setup
        : null;
    const attention = signals.filter((s) => s.kind === "attention");
    const discover = signals.filter((s) => s.kind === "discover");
    const setupRemaining = setup ? Math.max(0, setup.total - setup.completed) : 0;

    return {
      loading,
      enabled,
      setup,
      setupRemaining,
      attention,
      discover,
      // What the pill counts. Setup steps are included because to a new
      // institute they ARE the work — a pill reading "0" beside a half-finished
      // setup would be the surface contradicting itself.
      todoCount: attention.length + setupRemaining,
      // The pill's colour comes from the most urgent thing behind it, so its
      // tone is a promise about what is inside rather than decoration.
      leadTone: attention[0]?.tone || (setup ? "brand" : discover.length ? "brand" : null),
      hasAnything: !!setup || signals.length > 0,
      dismiss,
      reload,
    };
  }, [data, pending, loading, enabled, dismiss, reload]);
}
